import React, { useEffect, useState } from 'react';
import Protected from './Protected';

// CircleOfFifths component
// Expects backend endpoints (returning JSON) exposed from the MusicEngine API:
// GET /api/music/circle?preferFlats={true|false}
// -> [{ name: string, accidentals: number, isFlat: boolean }, ...]
// GET /api/music/scale?root={name}&type={major|minor}&preferFlats={true|false}
// -> [ "C", "D", ... ]
// GET /api/music/chord?root={name}&type={triad|seventh}&quality={Major|Minor|Diminished|Augmented|Major7|Dominant7|...}&preferFlats={true|false}
// -> [ "C", "E", "G" ]

export default function CircleOfFifths() {
    const [preferFlats, setPreferFlats] = useState(false);
    const [circle, setCircle] = useState([]);
    const [selected, setSelected] = useState(null);
    const [scale, setScale] = useState([]);
    const [scaleMinor, setScaleMinor] = useState([]);
    const [triad, setTriad] = useState([]);
    const [seventh, setSeventh] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [triadQuality, setTriadQuality] = useState('Major');
    const [seventhQuality, setSeventhQuality] = useState('Major7');

    const triadQualities = ['Major', 'Minor', 'Diminished', 'Augmented'];
    const seventhQualities = ['Major7', 'Dominant7', 'Minor7', 'HalfDiminished7', 'Diminished7'];

    useEffect(() => {
        fetchCircle();
    }, [preferFlats]);

    async function fetchCircle() {
        setError(null);
        try {
            setLoading(true);
            const res = await fetch(`/api/music/circle?preferFlats=${preferFlats}`);
            if (!res.ok) throw new Error(`Failed to load circle: ${res.status}`);
            const data = await res.json();
            setCircle(data || []);
            if (data && data.length) setSelected(data[0].name);
        } catch (ex) {
            setError(ex.message);
            setCircle([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (selected) {
            fetchScaleAndChords(selected);
        } else {
            setScale([]);
            setTriad([]);
            setSeventh([]);
        }
    }, [selected, preferFlats, triadQuality, seventhQuality]);

    async function fetchScaleAndChords(root) {
        setError(null);
        try {
            setLoading(true);
            const scaleRes = await fetch(`/api/music/scale?root=${encodeURIComponent(root)}&type=major&preferFlats=${preferFlats}`);
            const scaleMinor = await fetch(`/api/music/scale?root=${encodeURIComponent(root)}&type=minor&preferFlats=${preferFlats}`);
            const triadRes = await fetch(`/api/music/chord?root=${encodeURIComponent(root)}&type=triad&quality=${triadQuality}&preferFlats=${preferFlats}`);
            const seventhRes = await fetch(`/api/music/chord?root=${encodeURIComponent(root)}&type=seventh&quality=${seventhQuality}&preferFlats=${preferFlats}`);

            if (!scaleRes.ok) throw new Error(`Failed to load scale: ${scaleRes.status}`);
            if (!scaleMinor.ok) throw new Error(`Failed to load minor scale: ${scaleMinor.status}`);
            if (!triadRes.ok) throw new Error(`Failed to load triad: ${triadRes.status}`);
            if (!seventhRes.ok) throw new Error(`Failed to load seventh: ${seventhRes.status}`);

            const scaleJson = await scaleRes.json();
            const scaleMinorJson = await scaleMinor.json();
            const triadJson = await triadRes.json();
            const seventhJson = await seventhRes.json();

            setScale(scaleJson || []);
            setScaleMinor(scaleMinorJson || []);
            setTriad(triadJson || []);
            setSeventh(seventhJson || []);
        } catch (ex) {
            setError(ex.message);
            setScale([]);
            setScaleMinor([]);
            setTriad([]);
            setSeventh([]);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Protected>
            <div className="dashed-border">
                <h1 className="font-thin">Music Calculator</h1>
            </div>

            <div className="row mt-3">
                <div className="col-md-3">
                    <label htmlFor="TriadQuality" className="form-label fw-bold">Triad Quality</label>
                    <select name="TriadQuality" deafult="Major" onChange={e => setTriadQuality(e.target.selectedOptions[0].value)} className="form-control">
                        {triadQualities.map(i =>
                            <option key={i}>{i}</option>
                        )}
                    </select>
                </div>
                <div className="col-md-3">
                    <label htmlFor="SeventhQuality" className="form-label fw-bold">Seventh Quality</label>
                    <select name="SeventhQuality" deafult="Major7" onChange={e => setSeventhQuality(e.target.selectedOptions[0].value)} className="form-control">
                        {seventhQualities.map(i =>
                            <option key={i}>{i}</option>
                        )}
                    </select>
                </div>
                <div className="col-md-3">
                    <div class="btn-group mt-4" role="group" aria-label="Basic checkbox toggle button group">
                        <input type="checkbox" checked={preferFlats} onChange={e => setPreferFlats(e.target.checked)} class="btn-check" id="btnPreferFlats" autocomplete="off"></input>
                        <label class="btn btn-outline-primary mt-2 rounded" for="btnPreferFlats">Prefer Flats</label>
                    </div>
                </div>
            </div>
            {error && <div className="txt-danger">{error}</div>}

            <div className="row mt-2 mb-2">
                {circle.map(k => (
                    <div className="col-md-1 col-4">
                        <div key={k.name} onClick={() => setSelected(k.name)} className="btn btn-secondary m-2">
                            <div>{k.name}</div>
                            <div>{k.accidentals} {k.isFlat ? 'flat(s)' : 'sharp(s)'}</div>
                        </div>
                    </div>
                ))}
            </div>

            {selected && (
                <div className="row">
                    <h4>Selected: {selected}</h4>
                    <div className="col-md-6 col-12">
                        <strong>Major scale</strong>
                        <div className="fs-3">{scale.length ? scale.join(' / ') : 'n/a'}</div>
                    </div>
                    <div className="col-md-6 col-12">
                        <strong>Minor scale</strong>
                        <div className="fs-3">{scaleMinor.length ? scaleMinor.join(' / ') : 'n/a'}</div>
                    </div>
                    <div className="col-md-6 col-12">
                        <strong>Major triad</strong>
                        <div className="fs-3">{triad.length ? triad.join(' / ') : 'n/a'}</div>
                    </div>
                    <div className="col-md-6 col-12">
                        <strong>Major7th</strong>
                        <div className="fs-3">{seventh.length ? seventh.join(' / ') : 'n/a'}</div>
                    </div>
                </div>
            )}
        </Protected>
    );
}